/**
 * Header sections scroll behavior
 * Hides header-sections container on scroll down, shows on scroll up
 * Works with the entire header-sections container (includes announcement bars, stock counters, etc.)
 */
document.addEventListener('DOMContentLoaded', function() {
  let lastScroll = window.scrollY;

  function initHeader() {
    const headerSections = document.querySelector('.header-sections');
    if (!headerSections) return;

    let ticking = false;

    // Calculate and set header sections height as CSS variable
    updateHeaderHeight(headerSections);

    // Calculate and set header sections offset for sticky positioning
    updateHeaderOffset(headerSections);

    // Recalculate header sections height on resize
    let resizeTimeout;
    window.addEventListener('resize', function() {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(function() {
        updateHeaderHeight(headerSections);
        updateHeaderOffset(headerSections);
      }, 150);
    });

    // Throttle scroll events using requestAnimationFrame
    window.addEventListener('scroll', function() {
      if (!ticking) {
        window.requestAnimationFrame(function() {
          handleScroll(headerSections);
          closeMegaMenus();
          ticking = false;
        });
        ticking = true;
      }
    });

    // Update cart count when cart changes
    document.addEventListener('cart:updated', function(event) {
      updateCartCount(event.detail);
    });

    // Setup click-only mega menu toggle
    setupMegaMenuClick();

    // Setup mobile menu toggle
    setupMobileMenu();
  }

  /**
   * Handle scroll behavior
   */
  function handleScroll(headerSections) {
    const currentScroll = window.scrollY;
    const headerSectionsHeight = headerSections.offsetHeight;

    // Don't hide if at the top
    if (currentScroll <= headerSectionsHeight) {
      headerSections.classList.remove('hide');
      lastScroll = currentScroll;
      return;
    }

    // Scrolling down - hide header sections
    if (currentScroll > lastScroll && currentScroll > headerSectionsHeight) {
      headerSections.classList.add('hide');
    }
    // Scrolling up - show header sections
    else if (currentScroll < lastScroll) {
      headerSections.classList.remove('hide');
    }

    lastScroll = currentScroll;
  }

  /**
   * Calculate and set header sections height as CSS variable
   */
  function updateHeaderHeight(headerSections) {
    const headerSectionsHeight = headerSections.offsetHeight;
    document.documentElement.style.setProperty('--header-height', headerSectionsHeight + 'px');
  }

  /**
   * Calculate header sections offset based on announcement bars above it
   * Note: This is now handled by the header-sections container itself
   */
  function updateHeaderOffset(headerSections) {
    // Since header-sections contains all header-related sections including announcement bars,
    // we don't need to calculate offset separately - the container handles it
    // This function is kept for potential future use but currently does nothing
  }

  /**
   * Close all open mega menus on scroll
   */
  function closeMegaMenus() {
    const activeMenuItems = document.querySelectorAll('.header__nav-item.is-open');
    activeMenuItems.forEach(function(item) {
      item.classList.remove('is-open');
    });
  }

  /**
   * Setup click-only mega menu toggle (DESKTOP ONLY)
   * Toggles menu on link click, closes other menus
   * Disabled in theme editor to allow block selection
   */
  function setupMegaMenuClick() {
    // Don't apply click behavior in theme editor
    if (Shopify.designMode) return;

    const menuItems = document.querySelectorAll('.header__nav-item');
    
    menuItems.forEach(function(item) {
      const megaMenu = item.querySelector('.header__mega-menu');
      if (!megaMenu) return;

      const link = item.querySelector('.header__nav-link--dropdown');
      if (!link) return;

      // Toggle menu on click (desktop only - check window width)
      link.addEventListener('click', function(e) {
        // Only handle on desktop (> 1200px)
        if (window.innerWidth <= 1200) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        const isCurrentlyOpen = item.classList.contains('is-open');
        
        // Close all menus first
        menuItems.forEach(function(otherItem) {
          otherItem.classList.remove('is-open');
          const trigger = otherItem.querySelector('.header__nav-link--dropdown');
          if (trigger) trigger.setAttribute('aria-expanded', 'false');
        });
        
        // Toggle current menu (open if it was closed)
        if (!isCurrentlyOpen) {
          item.classList.add('is-open');
          link.setAttribute('aria-expanded', 'true');
        }
      });

      // Stop clicks inside mega menu from closing it
      megaMenu.addEventListener('click', function(e) {
        e.stopPropagation();
      });
    });

    // Close menus when clicking outside
    document.addEventListener('click', function(e) {
      const clickedInsideNav = e.target.closest('.header__nav');
      if (!clickedInsideNav) {
        menuItems.forEach(function(item) {
          item.classList.remove('is-open');
          const trigger = item.querySelector('.header__nav-link--dropdown');
          if (trigger) trigger.setAttribute('aria-expanded', 'false');
        });
      }
    });
  }

  /**
   * Setup mobile menu toggle
   * Opens/closes header nav as mobile menu and manages body scroll
   */
  function setupMobileMenu() {
    const headerNav = document.querySelector('.header__nav');
    if (!headerNav) return;

    const burgerButton = document.querySelector('[data-burger-toggle]');
    const header = document.querySelector('[data-header]');
    const body = document.body;

    // Toggle mobile menu
    if (burgerButton) {
      burgerButton.addEventListener('click', function() {
        const isOpen = headerNav.classList.contains('is-open');
        
        if (isOpen) {
          closeMobileMenu();
        } else {
          openMobileMenu();
        }
      });
    }

    // Open mobile menu
    function openMobileMenu() {
      headerNav.classList.add('is-open');
      burgerButton.setAttribute('aria-label', 'Close menu');
      burgerButton.classList.add('is-open');
      burgerButton.setAttribute('aria-expanded', 'true');
      body.style.setProperty('overflow', 'hidden', 'important'); // Prevent background scroll
      document.body.classList.add('is-new-header-open');
    }

    // Close mobile menu
    function closeMobileMenu() {
      headerNav.classList.remove('is-open');
      burgerButton.setAttribute('aria-label', 'Open menu');
      burgerButton.setAttribute('aria-expanded', 'false');
      burgerButton.classList.remove('is-open');
      body.style.setProperty('overflow', ''); // Restore scroll
      document.body.classList.remove('is-new-header-open');
      
      // Close any open submenus
      const openSubmenus = headerNav.querySelectorAll('.header__nav-item.has-submenu-open');
      openSubmenus.forEach(function(item) {
        item.classList.remove('has-submenu-open');
        const trigger = item.querySelector('.header__nav-link--dropdown');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
    }

    // Close on escape key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && headerNav.classList.contains('is-open')) {
        closeMobileMenu();
      }
    });

    // Handle mobile submenu navigation
    setupMobileSubmenus(headerNav);

    // Close when clicking outside the menu (but not on the header itself)
    document.addEventListener('click', function(e) {
      if (!headerNav.contains(e.target) && 
          !burgerButton.contains(e.target) &&
          header && !header.contains(e.target) &&
          headerNav.classList.contains('is-open')) {
        closeMobileMenu();
      }
    });

    // Close when clicking on regular links inside mobile menu (but not dropdown triggers)
    headerNav.addEventListener('click', function(e) {
      if (e.target.matches('a') && 
          !e.target.classList.contains('header__nav-link--dropdown') &&
          !e.target.closest('.header__mega-menu')) {
        closeMobileMenu();
      }
    });
  }

  /**
   * Setup mobile submenu navigation (MOBILE ONLY)
   * Handle clicking dropdown items on mobile to toggle accordion-style submenus
   */
  function setupMobileSubmenus(headerNav) {
    const dropdownItems = headerNav.querySelectorAll('.header__nav-item');

    dropdownItems.forEach(function(item) {
      const trigger = item.querySelector('.header__nav-link--dropdown');
      const megaMenu = item.querySelector('.header__mega-menu');
      
      if (!trigger || !megaMenu) return;

      // Click dropdown on mobile to toggle accordion
      trigger.addEventListener('click', function(e) {
        // Only handle on mobile (window width <= 1200px)
        if (window.innerWidth > 1200) return;
        
        // Only when mobile menu is open
        if (!headerNav.classList.contains('is-open')) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        const isCurrentlyOpen = item.classList.contains('has-submenu-open');
        
        // Find other open submenus (excluding current item)
        const allOpenSubmenus = headerNav.querySelectorAll('.header__nav-item.has-submenu-open');
        const otherOpenSubmenus = Array.from(allOpenSubmenus).filter(function(openItem) {
          return openItem !== item;
        });
        const hasOtherOpenSubmenus = otherOpenSubmenus.length > 0;
        
        // If opening a new submenu and there are other submenus open, close them first
        if (!isCurrentlyOpen && hasOtherOpenSubmenus) {
          // Close other submenus first
          otherOpenSubmenus.forEach(function(openItem) {
            openItem.classList.remove('has-submenu-open');
            const otherTrigger = openItem.querySelector('.header__nav-link--dropdown');
            if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
          });
          
          // Wait for close animation to complete (500ms) before opening new submenu
          setTimeout(function() {
            item.classList.add('has-submenu-open');
            trigger.setAttribute('aria-expanded', 'true');
            
            // Wait for DOM update and submenu expansion to progress, then scroll
            setTimeout(function() {
              scrollMenuItemToTop(item, headerNav);
            }, 300); // Wait for submenu expansion animation to progress
          }, 500); // Match CSS transition duration
        } else {
          // No other submenus to close, toggle immediately
          if (isCurrentlyOpen) {
            item.classList.remove('has-submenu-open');
            trigger.setAttribute('aria-expanded', 'false');
          } else {
            item.classList.add('has-submenu-open');
            trigger.setAttribute('aria-expanded', 'true');
            
            // Wait for DOM update and submenu expansion to progress, then scroll
            setTimeout(function() {
              scrollMenuItemToTop(item, headerNav);
            }, 300); // Wait for submenu expansion animation to progress
          }
        }
      });
    });
  }

  /**
   * Scroll menu item to top of mobile menu when submenu opens
   * One-time scroll when the submenu is first opened - no tracking after
   */
  function scrollMenuItemToTop(menuItem, headerNav) {
    // Calculate offset to account for mobile menu padding
    const menuPadding = 20; // Match CSS padding-top of .header__nav.is-open
    
    // Wait for DOM to update and layout to stabilize after submenu expansion
    // Use requestAnimationFrame to ensure we're measuring after layout updates
    requestAnimationFrame(function() {
      requestAnimationFrame(function() {
        // Get the nav list container (parent of menu items)
        const navList = headerNav.querySelector('.header__nav-list');
        if (!navList) return;
        
        // Get the current scroll position of the nav container
        const currentScrollTop = headerNav.scrollTop;
        
        // Get bounding rectangles relative to viewport
        const navRect = headerNav.getBoundingClientRect();
        const itemRect = menuItem.getBoundingClientRect();
        
        // Calculate the item's position relative to the container's scrollable content
        // itemRect.top is relative to viewport, navRect.top is container's top in viewport
        // Adding currentScrollTop gives us the position in the scrollable content
        const itemTopInScrollContent = (itemRect.top - navRect.top) + currentScrollTop;
        
        // Calculate target scroll position: item at top minus padding
        const targetScrollTop = itemTopInScrollContent - menuPadding;
        
        // Ensure we don't scroll to negative values
        const finalScrollTop = Math.max(0, targetScrollTop);
        
        // Scroll to the calculated position
        headerNav.scrollTo({
          top: finalScrollTop,
          behavior: 'smooth'
        });
      });
    });
  }

  /**
   * Update cart count badge
   */
  function updateCartCount(cart) {
    const cartIcon = document.querySelector('[data-cart-icon]');
    if (!cartIcon) return;

    const badge = cartIcon.querySelector('[data-cart-count]');
    const itemCount = cart.item_count || 0;

    if (itemCount > 0) {
      if (badge) {
        badge.textContent = itemCount;
      } else {
        const newBadge = document.createElement('span');
        newBadge.className = 'header__cart-badge';
        newBadge.setAttribute('data-cart-count', '');
        newBadge.textContent = itemCount;
        cartIcon.appendChild(newBadge);
      }
    } else if (badge) {
      badge.remove();
    }
  }

  initHeader();
});

// Theme editor support
if (Shopify.designMode) {
  initThemeEditorSupport();
}

/**
 * Handle theme editor events
 * Shows mega menu dropdown when menu item or any child block is selected
 */
function initThemeEditorSupport() {
  // Show mega menu when menu item or child block is selected
  document.addEventListener('shopify:block:select', function(event) {
    const selectedBlock = event.target;
    
    // Find the parent menu item (either the block itself or its ancestor)
    const menuItem = selectedBlock.classList.contains('header__nav-item') 
      ? selectedBlock 
      : selectedBlock.closest('.header__nav-item');
    
    if (menuItem) {
      const megaMenu = menuItem.querySelector('.header__mega-menu');
      if (megaMenu) {
        menuItem.classList.add('header__nav-item--editor-active');
      }
    }
  });

  // Also handle block load events
  document.addEventListener('shopify:block:load', function(event) {
    const selectedBlock = event.target;
    
    const menuItem = selectedBlock.classList.contains('header__nav-item') 
      ? selectedBlock 
      : selectedBlock.closest('.header__nav-item');
    
    if (menuItem) {
      const megaMenu = menuItem.querySelector('.header__mega-menu');
      if (megaMenu) {
        menuItem.classList.add('header__nav-item--editor-active');
      }
    }
  });

  // Hide mega menu when block is deselected
  document.addEventListener('shopify:block:deselect', function(event) {
    const deselectedBlock = event.target;
    
    const menuItem = deselectedBlock.classList.contains('header__nav-item') 
      ? deselectedBlock 
      : deselectedBlock.closest('.header__nav-item');
    
    if (menuItem) {
      menuItem.classList.remove('header__nav-item--editor-active');
    }
  });
}

/**
 * Currency Selector Custom Element
 * Handles dropdown toggle, currency selection, and form submission
 */
class CurrencySelector extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
  }

  connectedCallback() {
    // Get elements
    this.trigger = this.querySelector('.currency-selector__trigger');
    this.dropdown = this.querySelector('.currency-selector__dropdown');
    this.options = this.querySelectorAll('.currency-selector__option');
    this.form = this.querySelector('.currency-selector__form');
    this.currencyInput = this.querySelector('.currency-selector__input');
    this.selectedDisplay = this.querySelector('.currency-selector__selected');

    if (!this.trigger || !this.dropdown || !this.form || !this.currencyInput) return;

    // Mark current option as selected
    const currentCurrency = this.currencyInput.value;
    this.options.forEach(option => {
      if (option.dataset.currency === currentCurrency) {
        option.setAttribute('data-selected', 'true');
      }
    });

    // Bind event listeners
    this.bindEvents();
  }

  bindEvents() {
    // Toggle dropdown on trigger click
    this.trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleDropdown();
    });

    // Handle option selection
    this.options.forEach(option => {
      option.addEventListener('click', (e) => {
        e.stopPropagation();
        this.selectCurrency(option.dataset.currency);
      });
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target) && this.isOpen) {
        this.closeDropdown();
      }
    });

    // Close dropdown on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeDropdown();
        this.trigger.focus();
      }
    });

    // Keyboard navigation for options
    this.dropdown.addEventListener('keydown', (e) => {
      const focusedOption = document.activeElement;
      
      if (!focusedOption.matches('.currency-selector__option')) return;
      
      const optionsArray = Array.from(this.options);
      const currentIndex = optionsArray.indexOf(focusedOption);
      
      switch(e.key) {
        case 'ArrowDown':
          e.preventDefault();
          const nextIndex = (currentIndex + 1) % optionsArray.length;
          optionsArray[nextIndex].focus();
          break;
          
        case 'ArrowUp':
          e.preventDefault();
          const prevIndex = (currentIndex - 1 + optionsArray.length) % optionsArray.length;
          optionsArray[prevIndex].focus();
          break;
          
        case 'Enter':
        case ' ':
          e.preventDefault();
          focusedOption.click();
          break;
      }
    });
  }

  toggleDropdown() {
    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown() {
    this.isOpen = true;
    this.setAttribute('data-open', 'true');
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  closeDropdown() {
    this.isOpen = false;
    this.setAttribute('data-open', 'false');
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  selectCurrency(currency) {
    // Update hidden input
    this.currencyInput.value = currency;
    
    // Update display
    this.selectedDisplay.textContent = currency;
    
    // Update selected state on options
    this.options.forEach(option => {
      if (option.dataset.currency === currency) {
        option.setAttribute('data-selected', 'true');
      } else {
        option.removeAttribute('data-selected');
      }
    });
    
    // Close dropdown
    this.closeDropdown();
    
    // Submit form to change currency
    this.form.submit();
  }
}

// Register the custom element
customElements.define('currency-selector', CurrencySelector);


